import React from 'react';
import { Button } from '@components/Button';
import { Columns } from '@components/Columns';
import { PlaceholderCloudImage } from '@components/CloudImage';
import { Flex } from '@components/Flex';
import { Text } from '@components/typography';
import { PhotoCredit } from '@components/PhotoCredit';
import { useHomeHeroSectionController } from '@sections/home/HomeHeroSection/HomeHeroSection.controller';
import {
  ButtonsContainer,
  HeroLottieContainer,
  HeroTextPairing,
  HeroVideoContainer,
  HeroVideoWrapper,
  Step1TextBlockWrapper,
  Step2TextWrapper,
  StyledSection,
  UIMagicWrapper,
  MobBoxContainer,
  HeroVideo,
  TextWrapper,
  SlurpeeBgStrip,
  FlexContainer,
  ColumnContent,
  OverlayDiv,
  WaveImgContainer,
  DesktopBottomWaveImage,
  MobileBottomWaveImage,
} from '@sections/home/HomeHeroSection/HomeHeroSection.styles';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import ReactHtmlParser from 'react-html-parser';
import { getRootUrl } from '@utils/urlHelpers.util';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { VideoPlayer } from '@components/VideoPlayer';
import { useTranslation } from 'react-i18next';

interface HeroVideoSectionProps {
  onHeroAnimationProgress?: (progress: number) => void;
}

export const HomeHeroSection: React.FC<HeroVideoSectionProps> = ({ onHeroAnimationProgress }) => {
  const {
    sectionRef,
    uiMagicWrapperRef,
    heroVideoContainerRef,
    heroVideoWrapperRef,
    step1TextBlockRef,
    step2TextWrapperRef,
    heroLottieRef,
    isUiAssetsLoaded,
    videoUrl,
    waveRef,
    isVisible,
    isVisibleMobile,
    setIsVisible,
    setIsVisibleMobile,
  } = useHomeHeroSectionController({
    onHeroAnimationProgress,
  });
  const { t } = useTranslation('home');

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <StyledSection as="section" ref={sectionRef} id="Home_Hero">
        <LazyLoadWrapper beforeLoad={() => setIsVisible(true)} threshold={0}>
          <div />
        </LazyLoadWrapper>
        <HeroVideoContainer ref={heroVideoContainerRef}>
          <HeroVideoWrapper ref={heroVideoWrapperRef}>
            <SlurpeeBgStrip
              className="home-hero-lottie-element"
              src={'home/hero/bodymovin/slurpee/bg_stripping.jpg'}
              alt="Couple in the city"
              isEagerLoading
              optimization="aggressive"
            />
            <HeroLottieContainer className="home-hero-lottie-element" ref={heroLottieRef}>
              <PhotoCredit color="white" id="photoCreditHome" />
            </HeroLottieContainer>
            <div id="homepage-hero-video-overlay" />
            {isVisible && (
              <VideoPlayer
                muted
                loop
                autoPlay
                preload="auto"
                src={videoUrl}
                poster={cloudAssetUrl('home/hero/joy-header-static.jpg', { optimization: 'aggressive' })}
                playsInline
              />
            )}
            <WaveImgContainer ref={waveRef}>
              <DesktopBottomWaveImage
                src="home/hero/wave.svg"
                alt={t('sections.hero.waveAlt')}
                isEagerLoading
                width="100%"
                optimization="aggressive"
              />
            </WaveImgContainer>
          </HeroVideoWrapper>
        </HeroVideoContainer>
        <Step2TextWrapper ref={step2TextWrapperRef}>
          <Text variant="hed40" color="yellow5" tagName="h3" textAlign="center">
            {t('sections.hero.createAWeddingWebsite')}
          </Text>
        </Step2TextWrapper>
        <Step1TextBlockWrapper verticalAlign="center">
          <Columns.Column width={12}>
            <Flex ref={step1TextBlockRef} flexDirection={'column'} alignItems={'center'}>
              <HeroTextPairing
                id="wedding_website_hero_text_block"
                title={ReactHtmlParser(t('sections.hero.titleV2'))}
                titleVariant="hed72Hero"
                titleColor="purple1"
                body={t('sections.hero.step1ContentV2')}
                bodyVariant="dek24"
                bodyColor="purple1"
                bodyTagName="h1"
                marginTop={[15, 0]}
                marginBottom={[10]}
                alignment={'center'}
              />
              <ButtonsContainer>
                <Button id="homeHeroGetStartedBtn" variant="primary" forwardedAs={'a'} href={getRootUrl('/createwedding')}>
                  {t('sections.hero.getStartedBtn')}
                </Button>
                <Button id="homeHeroFindCoupledBtn" variant="darkfindACoupleBtn" forwardedAs={'a'} href="/find">
                  {t('sections.hero.findEventBtn')}
                </Button>
              </ButtonsContainer>
            </Flex>
          </Columns.Column>
        </Step1TextBlockWrapper>
        <UIMagicWrapper ref={uiMagicWrapperRef}>
          <PlaceholderCloudImage
            src="home/ui-magic/colors.png"
            style={{ top: '4%', left: '58%', maxHeight: '15%' }}
            className="ui-magic-img-top"
            alt={t('sections.hero.uiMagic.colorsAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.sendReminder')}
            className="ui-magic-img-top"
            style={{ top: '0%', left: '25%' }}
            alt={t('sections.hero.uiMagic.sendReminderAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.statuses')}
            className="ui-magic-img-top-right"
            style={{ top: '12%', right: '1%', maxWidth: '12%' }}
            alt={t('sections.hero.uiMagic.statusesAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.std')}
            style={{ top: '8%', right: '14%' }}
            className="ui-magic-img-top-right"
            alt={t('sections.hero.uiMagic.saveTheDateAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.hotelBooking')}
            className="ui-magic-img-right"
            style={{ top: '42%', right: '0%' }}
            alt={t('sections.hero.uiMagic.hotelBookingAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.registryTypes')}
            style={{ top: '40%', right: '25%', zIndex: 1 }}
            className="ui-magic-img-right"
            alt={t('sections.hero.uiMagic.registryTypesAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.rehearsalDinner')}
            className="ui-magic-img-bottom-right"
            style={{ bottom: '2%', right: '1%', maxWidth: '15%' }}
            alt={t('sections.hero.uiMagic.rehearsalDinnerAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.songRequests')}
            className="ui-magic-img-bottom-right"
            style={{ bottom: '5%', right: '18%', maxWidth: '15%' }}
            alt={t('sections.hero.uiMagic.songRequestsAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.table-12')}
            style={{ bottom: '1%', right: '37.5%', maxWidth: '25%' }}
            className="ui-magic-img-bottom"
            alt={t('sections.hero.uiMagic.weddingTableGroupAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.rsvpcard')}
            style={{ bottom: '-5%', left: '1%' }}
            className="ui-magic-img-bottom-left"
            alt={t('sections.hero.uiMagic.rsvpFormAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.searchbar')}
            style={{ bottom: '10%', left: '18%', maxWidth: '17%' }}
            className="ui-magic-img-bottom-left"
            alt={t('sections.hero.uiMagic.guestsFilterAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.map')}
            className="ui-magic-img-left"
            style={{ top: '45%', left: '-10%' }}
            alt={t('sections.hero.uiMagic.mapAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.countSummary')}
            style={{ top: '40%', left: '25%', zIndex: 1 }}
            className="ui-magic-img-left"
            alt={t('sections.hero.uiMagic.guestsCountSummaryAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
          <PlaceholderCloudImage
            src={t('sections.hero.assets.rsvpTogether')}
            className="ui-magic-img-top-left"
            style={{ top: '11%', left: '2%', maxWidth: '15%' }}
            alt={t('sections.hero.uiMagic.rsvpTogetherAlt')}
            isMainSrcShown={isUiAssetsLoaded}
            optimization="aggressive"
          />
        </UIMagicWrapper>
      </StyledSection>
      <MobBoxContainer>
        <LazyLoadWrapper beforeLoad={() => setIsVisibleMobile(true)} threshold={0}>
          <div />
        </LazyLoadWrapper>
        {isVisibleMobile && (
          <HeroVideo
            muted
            loop
            autoPlay
            preload="auto"
            src={videoUrl}
            poster={cloudAssetUrl('home/hero/joy-header-static.jpg', { optimization: 'aggressive' })}
            playsInline
          />
        )}
        <OverlayDiv />
        <TextWrapper>
          <ColumnContent width={12}>
            <FlexContainer flexDirection="column" alignItems="center">
              <HeroTextPairing
                id="wedding_website_hero_text_block"
                title={ReactHtmlParser(t('sections.hero.titleV2'))}
                titleVariant="hed72Hero"
                titleColor="purple1"
                body={t('sections.hero.step1ContentV2')}
                bodyVariant="dek24"
                bodyColor="purple1"
                bodyTagName="h1"
                alignment={'center'}
              />
              <ButtonsContainer flexWrap="wrap">
                <Button id="mobileHomeHeroGetStartedBtn" variant="darkGetStartedBtn" forwardedAs={'a'} href={getRootUrl('/createwedding')}>
                  {t('sections.hero.getStartedBtn')}
                </Button>
                <Button id="mobileHomeHeroFindCoupledBtn" variant="darkfindACoupleBtn" forwardedAs={'a'} href={getRootUrl('/find')}>
                  {t('sections.hero.findEventBtn')}
                </Button>
              </ButtonsContainer>
            </FlexContainer>
          </ColumnContent>
        </TextWrapper>
        <WaveImgContainer ref={waveRef}>
          <MobileBottomWaveImage
            src="home/hero/wave-mobile.svg"
            alt={t('sections.hero.waveAlt')}
            isEagerLoading
            width="106%"
            optimization="aggressive"
          />
        </WaveImgContainer>
      </MobBoxContainer>
    </div>
  );
};
